import { PaletteMode } from '@mui/material';
import Colors from 'assets/tokens/colors.json';

const color = Colors;

const lightPalette = {
    primary: {
        ultraLight: `${color.pds['teal-10'].value}`,
        light: `${color.pds['teal-50'].value}`,
        main: `${color.pds['teal-100'].value}`,
        dark: `${color.pds['teal-120'].value}`,
        accent: `${color.pds['teal-100'].value}`,
    },
    secondary: {
        light: `${color.pds['gray-10'].value}`,
        main: `${color.pds['gray-100'].value}`,
        dark: `${color.pds['gray-120'].value}`,
    },
    info: {
        light: `${color.pds['gray-70'].value}`,
        main: `${color.pds['gray-30'].value}`,
        dark: `${color.pds['gray-40'].value}`,
    },
    success: {
        main: `${color.pds['green-30'].value}`,
        status: `${color.pds['green-50'].value}`
    },
    successDialog: {
        light: `${color.pds['green-10'].value}`,
        main: `${color.pds['green-70'].value}`,
        dark: `${color.pds['green-100'].value}`,
        text: `${color.pds['gray-120'].value}`
    },
    errorDialog: {
        light: `${color.pds['red-10'].value}`,
        middle: `${color.pds['red-30'].value}`,
        main: `${color.pds['red-70'].value}`,
        dark: `${color.pds['red-100'].value}`,
        text: `${color.pds['gray-120'].value}`
    },
    error: {
        light: `${color.pds['red-30'].value}`,
        main: `${color.pds['red-70'].value}`,
        dark: `${color.pds['red-100'].value}`,
    },
    warning: {
        ultraLight: `${color.pds['yellow-10'].value}`,
        light: `${color.pds['yellow-50'].value}`,
        main: `${color.pds['yellow-100'].value}`,
        dark: `${color.pds['yellow-120'].value}`,
    },
    background: {
        default: '#fafafa',
        paper: '#fff',
    },
};

const darkPalette = {
    // Deep Ocean dark palette
    primary: {
        ultraLight: `${color.pds['dark-accent-ultralight'].value}`,
        light: `${color.pds['dark-sidebar-hover'].value}`,
        main: `${color.pds['dark-sidebar'].value}`,
        dark: `${color.pds['dark-sidebar-active'].value}`,
        accent: `${color.pds['dark-accent'].value}`,
    },
    secondary: {
        light: `${color.pds['dark-bg'].value}`,
        main: `${color.pds['dark-text-secondary'].value}`,
        dark: `${color.pds['dark-text-primary'].value}`,
    },
    info: {
        light: `${color.pds['dark-text-secondary'].value}`,
        main: `${color.pds['dark-border'].value}`,
        dark: `${color.pds['dark-border'].value}`,
    },
    success: {
        main: `${color.pds['green-30'].value}`,
        status: `${color.pds['green-50'].value}`,
    },
    error: {
        light: `${color.pds['red-30'].value}`,
        main: `${color.pds['red-70'].value}`,
        dark: `${color.pds['red-100'].value}`,
    },
    warning: {
        ultraLight: `${color.pds['yellow-10'].value}`,
        light: `${color.pds['yellow-50'].value}`,
        main: `${color.pds['yellow-100'].value}`,
        dark: `${color.pds['yellow-120'].value}`,
    },
    successDialog: {
        light: `${color.pds['dark-success-bg'].value}`,
        main: `${color.pds['green-70'].value}`,
        dark: `${color.pds['green-100'].value}`,
        text: `${color.pds['dark-text-primary'].value}`,
    },
    errorDialog: {
        light: `${color.pds['dark-error-bg'].value}`,
        middle: `${color.pds['red-30'].value}`,
        main: `${color.pds['red-70'].value}`,
        dark: `${color.pds['red-100'].value}`,
        text: `${color.pds['dark-text-primary'].value}`,
    },
    background: {
        default: `${color.pds['dark-bg'].value}`,
        paper: `${color.pds['dark-surface'].value}`,
    },
};

const PalmPalette = (mode: PaletteMode) => {
    const isDark = mode === 'dark';
    const palette = isDark ? darkPalette : lightPalette;

    return {
        palette: {
            mode,
            ...palette,
        },
        typography: {
            fontFamily: `
                'Source Sans Pro',
                -apple-system,
                BlinkMacSystemFont,
                'Segoe UI',
                'Roboto',
                'Oxygen',
                'Ubuntu',
                'Cantarell',
                'Fira Sans',
                'Droid Sans',
                'Helvetica Neue',
                sans-serif`,
        },
        components: {
            MuiPaper: {
                styleOverrides: {
                    root: isDark ? {
                        backgroundImage: 'none',
                    } : {},
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: isDark ? {
                        backgroundColor: palette.background.default,
                    } : {},
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: isDark ? {
                        color: palette.secondary.main,
                        '&.Mui-selected': {
                            color: palette.primary.accent,
                        },
                    } : {},
                },
            },
            MuiTabs: {
                styleOverrides: {
                    indicator: isDark ? {
                        backgroundColor: palette.primary.accent,
                    } : {},
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: isDark ? {
                        '&.MuiButton-contained': {
                            backgroundColor: `${color.pds['dark-button'].value}`,
                            color: '#fff',
                            '&:hover': {
                                backgroundColor: `${color.pds['dark-button-hover'].value}`,
                            },
                            '&.Mui-disabled': {
                                backgroundColor: palette.background.paper,
                                color: palette.secondary.main,
                            },
                        },
                        '&.MuiButton-outlined': {
                            color: palette.primary.accent,
                            borderColor: palette.primary.accent,
                            '&:hover': {
                                borderColor: palette.primary.accent,
                                backgroundColor: `${color.pds['dark-accent-hover-bg'].value}`,
                            },
                            '&.Mui-disabled': {
                                color: palette.secondary.main,
                                borderColor: palette.info.main,
                            },
                        },
                        '&.MuiButton-text': {
                            color: palette.primary.accent,
                        },
                    } : {},
                },
            },
            MuiFilledInput: {
                styleOverrides: {
                    root: isDark ? {
                        backgroundColor: palette.background.default,
                        '&:hover': {
                            backgroundColor: palette.background.default,
                        },
                        '&.Mui-focused': {
                            backgroundColor: palette.background.default,
                        },
                        '&.Mui-disabled': {
                            backgroundColor: palette.background.paper,
                        },
                        '&::after': {
                            borderBottomColor: palette.primary.accent,
                        },
                    } : {},
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    input: isDark ? {
                        '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
                            WebkitBoxShadow: `0 0 0 100px ${palette.background.paper} inset !important`,
                            WebkitTextFillColor: '#fff !important',
                        },
                    } : {},
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: isDark ? {
                        color: palette.secondary.main,
                        '&.Mui-focused': {
                            color: palette.primary.accent,
                        },
                    } : {},
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        '&.active': isDark ? {
                            borderLeftColor: palette.primary.accent,
                            borderRadius: 0,
                            '& .MuiListItemIcon-root svg': { color: palette.primary.accent },
                            '& .MuiListItemText-root': { color: palette.primary.accent },
                        } : {},
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: isDark ? {
                        backgroundColor: palette.background.paper,
                        border: `1px solid ${palette.info.dark}`,
                    } : {},
                },
            },
            MuiPopover: {
                styleOverrides: {
                    paper: isDark ? {
                        backgroundColor: palette.background.paper,
                        border: `1px solid ${palette.info.dark}`,
                    } : {},
                },
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: isDark ? {
                        color: palette.secondary.main,
                        '&.Mui-checked': {
                            color: palette.primary.accent,
                        },
                    } : {},
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: isDark ? {
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: palette.info.dark,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: palette.primary.accent,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: palette.primary.accent,
                        },
                        '&.Mui-disabled': {
                            backgroundColor: palette.background.paper,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: palette.info.dark,
                            },
                        },
                    } : {},
                },
            },
            MuiCircularProgress: {
                styleOverrides: {
                    root: isDark ? {
                        color: palette.primary.accent,
                    } : {},
                },
            },
            MuiLink: {
                styleOverrides: {
                    root: isDark ? {
                        color: palette.primary.accent,
                    } : {},
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    paper: isDark ? {
                        backgroundColor: palette.background.paper,
                        border: `1px solid ${palette.info.dark}`,
                    } : {},
                    listbox: isDark ? {
                        '& .MuiAutocomplete-option[aria-selected="true"]': {
                            backgroundColor: palette.background.paper,
                        },
                        '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
                            backgroundColor: palette.primary.light,
                        },
                        '& .MuiAutocomplete-option:hover, & .MuiAutocomplete-option[aria-selected="true"]:hover': {
                            backgroundColor: palette.primary.light,
                        },
                    } : {},
                },
            },
        },
    };
};

export { PalmPalette };
